import context from '@truckdown/components-systems';
import { ILocationQueryService, PromotionType } from '@truckdown/locations';
import { EditPromotionModel, PromotionModel, IPromotionManager } from '@truckdown/promotions';
import { defineComponent, ref, Ref, computed, onMounted } from "vue";
import validationError from '../../Validation/Error/Error.vue';
import { PromotionValidator } from '../Validation/PromotionValidator';
import locationLookup from '../LocationLookup/LocationLookup.vue';
import { showMessage, isCsrOrAdmin } from '../../Utilities/utilities'

interface IData {
    locationList: Ref<ISelectItem[]>,
    isLoading: Ref<boolean>,
    showError: Ref<boolean>,
    promotionValidator: Ref<any>,
    promotionToEdit: Ref<EditPromotionModel>,
    showLocationInput: Ref<boolean>,
    promotion: Ref<PromotionModel>,
    expiryDateInput: Ref<string>,
    noExpiryDate: Ref<boolean>,
    editTitle: Ref<string>,
    editMessage: Ref<string>,
    customerEdit: Ref<boolean>
};

interface ISelectItem {
    value: string,
    display: string,
    terms?: string
};

const getInitialData = function (props: any) {
    let promotionToEdit = {
        promotionId: props.promotion.id,
        title: props.promotion.edit?.title ?? props.promotion.title,
        message: props.promotion.edit?.message ?? props.promotion.message,
        type: props.promotion.type,
        applyToAllLocations: props.promotion.applyToAllLocations,
        locationIds: [],
        services: props.promotion.services,
        dealerCode: props.promotion.dealerCode,
        regionCode: props.promotion.regionCode,
        dailyLimit: props.promotion.funds.dailyLimit,
        site: props.promotion.site,
        expiry: props.promotion.expiry,
    } as EditPromotionModel;

    return {
        locationList: ref<ISelectItem[]>([]),
        isLoading: ref<boolean>(false),
        showError: ref<boolean>(false),
        promotionValidator: ref<any>(new PromotionValidator(promotionToEdit)),
        promotionToEdit: ref<EditPromotionModel>(promotionToEdit as EditPromotionModel),
        showLocationInput: ref<boolean>(true),
        promotion: ref<PromotionModel>(props.promotion),
        expiryDateInput: ref<any>(promotionToEdit.expiry == undefined ? "" : promotionToEdit.expiry?.toString().split('T')[0]),
        noExpiryDate: ref<boolean>(promotionToEdit.expiry == undefined ? true : false),
        editMessage: ref<string>(props.promotion.edit?.message ?? ""),
        editTitle: ref<string>(props.promotion.edit?.title ?? ""),
        customerEdit: ref<boolean>(isCsrOrAdmin() === false)
    } as IData;
}

const getComputed = function (props: any, data: IData) {
    return {
        selectedLocations: computed(() => {
            return data.locationList.value.map((val) => {
                return val.value;
            });
        })
    };
}

const getMethods = function (props: any, data: IData, ctx: any) {
    return {
        editPromotionCancelClick: function (): void {
            ctx.emit(promotionCancelEventName);
        },
        editPromotionSaveClick: async function (): Promise<void> {
            await savePromotion(data, props, ctx);
        },
        expiryChange: function () {
            if (data.expiryDateInput.value === undefined) {
                data.noExpiryDate.value = true;
                data.expiryDateInput.value = '';
                data.promotionToEdit.value.expiry = undefined;
            } else {
                data.noExpiryDate.value = false;
                data.promotionToEdit.value.expiry = new Date(data.expiryDateInput.value);
            }
        },
        noExpiryChange: function () {
            if (data.noExpiryDate.value == true) {
                data.expiryDateInput.value = '';
                data.promotionToEdit.value.expiry = undefined;
            }
        },
        selectLocation: function (location: ISelectItem) {
            data.locationList.value.push(location);
            data.promotionToEdit.value.locationIds.push(location.value);
        },
        removeLocation: function (location: ISelectItem) {
            let idx = data.locationList.value.indexOf(location);
            if (idx >= 0) {
                data.locationList.value.splice(idx, 1);
            }
            var idx2 = data.promotionToEdit.value.locationIds.indexOf(location.value);
            if (idx2 >= 0) {
                data.promotionToEdit.value.locationIds.splice(idx2, 1);
            }
        }
    }
}

const savePromotion = async function (data: IData, props: any, ctx: any): Promise<void> {
    if (!data.isLoading.value) {
        data.isLoading.value = true;
        try {
            if (isPromotionValid(data)) {
                let mgr = await context.getService<IPromotionManager>('IPromotionManager');

                if (data.noExpiryDate.value === false) {
                    data.promotionToEdit.value.expiry = new Date(data.expiryDateInput.value);
                } else {
                    data.promotionToEdit.value.expiry = undefined;
                }

                var response = await mgr.editPromotion(data.promotionToEdit.value);

                if (!response || !response.ok) {
                    console.log('Error editing promotion: ' + response.message);
                    showMessage("Error editing promotion", "danger", true, true);
                } else {
                    showMessage("Promotion was successfully edited.", "success", true, true);
                    ctx.emit(promotionUpdatedEventName, response.data);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            data.isLoading.value = false;
        }
    }
}

const isPromotionValid = function (data: IData): boolean {
    data.promotionValidator.value = new PromotionValidator(data.promotionToEdit.value);
    return data.promotionValidator.value.validate();
}

const promotionUpdatedEventName = 'promotion-update';
const promotionCancelEventName = 'cancel';

export default defineComponent({
    props: {
        promotion: {
            type: Object as () => PromotionModel,
            required: true
        }
    },
    components: {
        'error': validationError,
        'location-lookup': locationLookup
    },
    emits: [promotionUpdatedEventName, promotionCancelEventName],
    setup: function (props, ctx) {
        let data = getInitialData(props);
        let computed = getComputed(props, data);
        let methods = getMethods(props, data, ctx);

        onMounted(async () => {
            if (!props.promotion.applyToAllLocations
                && props.promotion.locationIds.length > 0) {
                try {
                    var locService = await context.getService<ILocationQueryService>('ILocationQueryService');
                    var locations = await locService.findLocations(props.promotion.locationIds);
                    data.locationList.value = locations.results.map((l) => {
                        return {
                            value: l.id,
                            display: `${l.name} - ${l.address.city},  ${l.address.regionCode}`,
                            terms: l.locationId.toString()
                        };
                    });
                    data.promotionToEdit.value.locationIds = props.promotion.locationIds;
                } catch (err) {
                    console.log(err);
                    data.promotionToEdit.value.locationIds = [];
                    data.locationList.value = [];
                }
            }
        });

        return {
            ...data,
            ...computed,
            ...methods,
            'PromotionType': PromotionType,
            'isCsrOrAdmin': isCsrOrAdmin
        };
    }
});